/**
 * KK-515 / KK-516 · AK-17 / AK-18 — Transfer ve İKİ TARAFLI DURUM MAKİNESİ.
 *
 *   taslak → gonderildi → teslim_alindi
 *                      ↘ farkli_teslim → (yönetim onayı) → kapali
 *
 * Tek adımda tamamlanan transfer YOKTUR: gönderim kaynaktan düşer, teslim
 * hedefe ekler. Teslim alınan miktar gönderilenden farklıysa transfer
 * farkli_teslim'e geçer, ayrı bir transfer_farki hareketi yazılır ve YÖNETİM
 * ONAYI bekler. FARK SESSİZCE KAPATILAMAZ — bu ekranın en üst bandı, onay
 * bekleyen farklar için ayrılmıştır.
 */
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alan,
  Buton,
  Kart,
  Rozet,
  Secim,
  Tablo,
  TabloBaslik,
  TabloGovde,
  Uyari,
} from "@kantin/ui";
import {
  ROL_ADI,
  Rol,
  SEBEP_HEDEF_DURUM,
  STOK_DURUM_ADI,
  TransferDurumu,
  TransferSebebi,
} from "@kantin/shared";
import { useYetkili } from "../../veri/oturum.js";
import { apiOku, apiYaz } from "./api.js";
import { barkodBul, noktaSecenekleri, useNoktalar, useUrunler } from "./kancalar.js";
import { UrunSecimi } from "./UrunSecimi.js";
import { transferBarkoduEkle } from "./transferBarkodu.js";
import { urunleriSuz } from "./urunArama.js";
import type { BarkodSonucu } from "./ortak.js";
import {
  BarkodAlani,
  AramaKutusu,
  IslemDurumu,
  MetinAlani,
  Sayfa,
  SayfaBasligi,
  Sekmeler,
  SorguDurumu,
  miktarGecerliMi,
  miktarGoster,
  miktarNormalize,
  tarihSaatGoster,
} from "./ortak.js";

// ---------------------------------------------------------------------------

interface TransferBasligi {
  id: string;
  durum: number;
  sebep: number;
  hedef_durum: number;
  hedef_durum_adi: string;
  kaynak_nokta_id: string;
  kaynak_nokta_ad: string;
  hedef_nokta_id: string;
  hedef_nokta_ad: string;
  gonderim_zamani: string | null;
  teslim_zamani: string | null;
  fark_onaylayan_id: string | null;
  fark_onay_zamani: string | null;
  fark_onayi_bekliyor: boolean;
  aciklama: string | null;
  olusma_zamani: string;
  satir_sayisi: number;
}

interface TransferSatiri {
  urun_id: string;
  urun_ad: string;
  gonderilen_miktar: string;
  teslim_alinan_miktar: string | null;
  fark: string | null;
  birim_maliyet: string;
}

interface TransferDetayi extends TransferBasligi {
  satirlar: TransferSatiri[];
}

const DURUM_ADI: Record<number, string> = {
  [TransferDurumu.TASLAK]: "Taslak",
  [TransferDurumu.GONDERILDI]: "Gönderildi",
  [TransferDurumu.TESLIM_ALINDI]: "Teslim alındı",
  [TransferDurumu.FARKLI_TESLIM]: "Farklı teslim",
  [TransferDurumu.IPTAL]: "İptal",
};

const DURUM_ROZETI: Record<number, "notr" | "bilgi" | "onay" | "hata"> = {
  [TransferDurumu.TASLAK]: "notr",
  [TransferDurumu.GONDERILDI]: "bilgi",
  [TransferDurumu.TESLIM_ALINDI]: "onay",
  [TransferDurumu.FARKLI_TESLIM]: "hata",
  [TransferDurumu.IPTAL]: "notr",
};

const SEBEP_ADI: Record<number, string> = {
  [TransferSebebi.DAGITIM]: "Depodan dağıtım",
  [TransferSebebi.IADE_SATILMAYAN]: "Satılmayan malın iadesi",
  [TransferSebebi.IADE_TARIH_YAKLASAN]: "Tarihi yaklaşan mal iadesi",
  [TransferSebebi.IADE_TARIH_GECMIS]: "Tarihi geçmiş mal iadesi",
  [TransferSebebi.NOKTALAR_ARASI]: "Noktalar arası transfer",
};

type Sekme = "fark" | "liste" | "yeni";

export function Transferler() {
  const [sekme, setSekme] = useState<Sekme>("fark");
  const [acikTransfer, setAcikTransfer] = useState<string | null>(null);

  const farkSorgu = useQuery({
    queryKey: ["transfer", "fark"],
    queryFn: () =>
      apiOku<{ transferler: TransferBasligi[] }>("/transfer", {
        durum: TransferDurumu.FARKLI_TESLIM,
        limit: 200,
      }),
  });

  const bekleyenFarklar = (farkSorgu.data?.transferler ?? []).filter(
    (t) => t.fark_onayi_bekliyor,
  );

  return (
    <Sayfa>
      <SayfaBasligi
        baslik="Transferler"
        aciklama="Depo ve noktalar arası mal hareketi. Gönderim ve teslim iki ayrı adımdır; tek adımda tamamlanan transfer yoktur."
      />

      <DurumMakinesi />

      <Sekmeler<Sekme>
        etiket="Transfer bölümleri"
        secili={sekme}
        sec={setSekme}
        sekmeler={[
          {
            deger: "fark",
            metin: "Onay bekleyen farklar",
            sayi: bekleyenFarklar.length,
          },
          { deger: "liste", metin: "Tüm transferler" },
          { deger: "yeni", metin: "Yeni transfer" },
        ]}
      />

      {sekme === "fark" && (
        <FarkKuyrugu
          sorgu={farkSorgu}
          transferler={bekleyenFarklar}
          ac={setAcikTransfer}
          acik={acikTransfer}
        />
      )}
      {sekme === "liste" && (
        <TransferListesi acik={acikTransfer} ac={setAcikTransfer} />
      )}
      {sekme === "yeni" && (
        <YeniTransfer
          olusturuldu={(id) => {
            setSekme("liste");
            setAcikTransfer(id);
          }}
        />
      )}
    </Sayfa>
  );
}

// ---------------------------------------------------------------------------

function DurumMakinesi() {
  const adimlar = [
    { ad: "Taslak", not: "Satırlar girildi, stok henüz dokunulmadı" },
    { ad: "Gönderildi", not: "Kaynak noktadan düştü" },
    { ad: "Teslim alındı", not: "Hedef noktaya eklendi" },
  ];
  return (
    <Kart baslik="Durum makinesi" sikisik>
      <ol className="m-0 flex list-none flex-wrap items-stretch gap-2 p-0">
        {adimlar.map((a, i) => (
          <li
            key={a.ad}
            className="flex min-w-[10rem] flex-1 items-center gap-2"
          >
            <div className="flex-1 rounded-md border border-cetvel bg-kagit-2 px-3 py-2">
              <p className="m-0 text-sm font-semibold text-murekkep">{a.ad}</p>
              <p className="m-0 text-xs text-murekkep-3">{a.not}</p>
            </div>
            {i < adimlar.length - 1 && (
              <span aria-hidden="true" className="text-murekkep-3">
                →
              </span>
            )}
          </li>
        ))}
        <li className="flex min-w-[14rem] flex-1 items-center gap-2">
          <div className="flex-1 rounded-lg border border-hata-cizgi bg-hata-yumusak px-3 py-2">
            <p className="m-0 text-sm font-semibold text-murekkep">
              Farklı teslim
            </p>
            <p className="m-0 text-xs text-murekkep-2">
              Teslim alınan ≠ gönderilen · <strong>yönetim onayı bekler</strong>
            </p>
          </div>
        </li>
      </ol>
    </Kart>
  );
}

// ---------------------------------------------------------------------------

function FarkKuyrugu({
  sorgu,
  transferler,
  acik,
  ac,
}: {
  sorgu: { isPending: boolean; error: unknown; refetch: () => unknown };
  transferler: TransferBasligi[];
  acik: string | null;
  ac: (id: string | null) => void;
}) {
  return (
    <>
      <Uyari tur="uyari" baslik="Fark sessizce kapatılamaz">
        Teslim alınan miktar gönderilenden farklıysa aradaki fark ayrı bir stok
        hareketi olarak yazılır ve <strong>onaylanana kadar açık kalır</strong>.
        Onay ekranında kimin sorumluluğunda olduğu (gönderen nokta / teslim alan
        nokta) açıkça görünür.
      </Uyari>

      <Kart
        baslik="Onay bekleyen farklar"
        ustBilgi={
          <span className="text-sm text-murekkep-3">
            <span className="rakam font-mono">{transferler.length}</span> kayıt
          </span>
        }
      >
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={transferler.length === 0}
          bosBaslik="Onay bekleyen fark yok"
          bosAciklama="Tüm teslimatlar gönderilen miktarla birebir kapandı."
          yenile={() => void sorgu.refetch()}
        >
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {transferler.map((t) => (
              <li key={t.id}>
                <TransferOzetSatiri
                  transfer={t}
                  acik={acik === t.id}
                  degistir={() => ac(acik === t.id ? null : t.id)}
                />
                {acik === t.id && <TransferDetayPaneli transferId={t.id} />}
              </li>
            ))}
          </ul>
        </SorguDurumu>
      </Kart>
    </>
  );
}

function TransferOzetSatiri({
  transfer,
  acik,
  degistir,
}: {
  transfer: TransferBasligi;
  acik: boolean;
  degistir: () => void;
}) {
  return (
    <button
      type="button"
      onClick={degistir}
      aria-expanded={acik}
      className={`flex w-full flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-left ${
        transfer.fark_onayi_bekliyor
          ? "border-hata-cizgi bg-hata-yumusak"
          : "border-cetvel bg-yuzey hover:bg-kagit-2"
      }`}
    >
      <span className="min-w-0">
        <span className="block font-semibold text-murekkep">
          {transfer.kaynak_nokta_ad}{" "}
          <span aria-hidden="true" className="text-murekkep-3">
            →
          </span>{" "}
          {transfer.hedef_nokta_ad}
        </span>
        <span className="block text-xs text-murekkep-3">
          #{transfer.id} · {SEBEP_ADI[transfer.sebep] ?? "Transfer"} ·{" "}
          {transfer.satir_sayisi} kalem ·{" "}
          {tarihSaatGoster(transfer.olusma_zamani)}
        </span>
      </span>
      <span className="flex items-center gap-2">
        <Rozet tur={DURUM_ROZETI[transfer.durum] ?? "notr"}>
          {DURUM_ADI[transfer.durum] ?? "Bilinmiyor"}
        </Rozet>
        {transfer.fark_onayi_bekliyor && (
          <Rozet tur="hata">onay bekliyor</Rozet>
        )}
        <span aria-hidden="true" className="text-murekkep-3">
          {acik ? "▲" : "▼"}
        </span>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------

function TransferListesi({
  acik,
  ac,
}: {
  acik: string | null;
  ac: (id: string | null) => void;
}) {
  const [durum, setDurum] = useState("");
  const [noktaId, setNoktaId] = useState("");
  const noktalar = useNoktalar();

  const sorgu = useQuery({
    queryKey: ["transfer", "liste", durum, noktaId],
    queryFn: () =>
      apiOku<{ transferler: TransferBasligi[] }>("/transfer", {
        durum: durum || undefined,
        nokta_id: noktaId || undefined,
        limit: 100,
      }),
  });

  const transferler = sorgu.data?.transferler ?? [];

  return (
    <>
      <Kart baslik="Süzgeçler">
        <div className="grid gap-3 md:grid-cols-3">
          <Secim
            etiket="Durum"
            value={durum}
            onChange={(e) => setDurum(e.target.value)}
            secenekler={[
              { deger: "", metin: "Tüm durumlar" },
              ...Object.entries(DURUM_ADI).map(([k, v]) => ({
                deger: k,
                metin: v,
              })),
            ]}
          />
          <Secim
            etiket="Nokta (kaynak veya hedef)"
            value={noktaId}
            onChange={(e) => setNoktaId(e.target.value)}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar)}
          />
        </div>
      </Kart>

      <Kart baslik="Transferler">
        <SorguDurumu
          yukleniyor={sorgu.isPending}
          hata={sorgu.error}
          bos={transferler.length === 0}
          bosBaslik="Transfer yok"
          bosAciklama="Bu süzgeçte transfer bulunmuyor."
          yenile={() => void sorgu.refetch()}
        >
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {transferler.map((t) => (
              <li key={t.id}>
                <TransferOzetSatiri
                  transfer={t}
                  acik={acik === t.id}
                  degistir={() => ac(acik === t.id ? null : t.id)}
                />
                {acik === t.id && <TransferDetayPaneli transferId={t.id} />}
              </li>
            ))}
          </ul>
        </SorguDurumu>
      </Kart>
    </>
  );
}

// ---------------------------------------------------------------------------

function TransferDetayPaneli({ transferId }: { transferId: string }) {
  const istemci = useQueryClient();
  const sorgu = useQuery({
    queryKey: ["transfer", transferId],
    queryFn: () => apiOku<TransferDetayi>(`/transfer/${transferId}`),
  });
  const [teslimAcik, setTeslimAcik] = useState(false);
  const [onayNotu, setOnayNotu] = useState("");
  // KK-516: farkı YALNIZCA yönetim onaylar. Transferi oluşturan nokta
  // sorumlusu kaydı görür ama kendi farkını kapatamaz.
  const farkOnaylayabilir = useYetkili(
    Rol.KANTIN_MUDURU,
    Rol.OKUL_YONETIMI,
    Rol.OKUL_SISTEM_YONETICISI,
  );

  const tazele = () => {
    void istemci.invalidateQueries({ queryKey: ["transfer"] });
    void istemci.invalidateQueries({ queryKey: ["stok"] });
  };

  const gonder = useMutation({
    mutationFn: () => apiYaz("POST", `/transfer/${transferId}/gonder`),
    onSuccess: tazele,
  });
  const iptal = useMutation({
    mutationFn: () => apiYaz("POST", `/transfer/${transferId}/iptal`),
    onSuccess: tazele,
  });
  const farkOnayla = useMutation({
    mutationFn: () =>
      apiYaz("POST", `/transfer/${transferId}/fark-onayla`, {
        ...(onayNotu.trim() ? { not: onayNotu.trim() } : {}),
      }),
    onSuccess: () => {
      setOnayNotu("");
      tazele();
    },
  });

  const t = sorgu.data;

  return (
    <div className="mt-2 rounded-md border border-cetvel bg-kagit-2 p-3">
      <SorguDurumu
        yukleniyor={sorgu.isPending}
        hata={sorgu.error}
        bos={!t}
        bosBaslik="Transfer bulunamadı"
        iskeletSatir={3}
        yenile={() => void sorgu.refetch()}
      >
        {t && (
          <div className="flex flex-col gap-4">
            <dl className="m-0 grid gap-3 text-sm md:grid-cols-4">
              <div>
                <dt className="text-xs text-murekkep-3">Sebep</dt>
                <dd className="m-0 text-murekkep">
                  {SEBEP_ADI[t.sebep] ?? "Transfer"}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-murekkep-3">Hedef kondisyon</dt>
                <dd className="m-0 text-murekkep">{t.hedef_durum_adi}</dd>
              </div>
              <div>
                <dt className="text-xs text-murekkep-3">Gönderim</dt>
                <dd className="m-0 text-murekkep">
                  {tarihSaatGoster(t.gonderim_zamani)}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-murekkep-3">Teslim</dt>
                <dd className="m-0 text-murekkep">
                  {tarihSaatGoster(t.teslim_zamani)}
                </dd>
              </div>
            </dl>

            {t.aciklama && (
              <p className="m-0 text-sm text-murekkep-2">{t.aciklama}</p>
            )}

            <Tablo>
              <TabloBaslik>
                <th scope="col">Ürün</th>
                <th scope="col">Gönderilen</th>
                <th scope="col">Teslim alınan</th>
                <th scope="col">Fark</th>
              </TabloBaslik>
              <TabloGovde>
                {t.satirlar.map((s) => {
                  const farkVar = s.fark !== null && s.fark !== "0.000";
                  return (
                    <tr key={s.urun_id}>
                      <td className="font-semibold text-murekkep">
                        {s.urun_ad}
                      </td>
                      <td className="rakam text-right font-mono">
                        {miktarGoster(s.gonderilen_miktar)}
                      </td>
                      <td className="rakam text-right font-mono">
                        {s.teslim_alinan_miktar === null
                          ? "—"
                          : miktarGoster(s.teslim_alinan_miktar)}
                      </td>
                      <td className="text-right">
                        {s.fark === null ? (
                          <span className="text-murekkep-3">—</span>
                        ) : farkVar ? (
                          <Rozet tur="hata">
                            <span className="rakam font-mono">
                              {miktarGoster(s.fark)}
                            </span>
                          </Rozet>
                        ) : (
                          <span className="rakam font-mono text-murekkep-3">
                            0
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </TabloGovde>
            </Tablo>

            {t.durum === TransferDurumu.FARKLI_TESLIM && (
              <div className="rounded-lg border border-hata-cizgi bg-hata-yumusak p-3">
                <h4 className="m-0 text-lg">Fark sorumluluğu</h4>
                <p className="m-0 mt-1 text-sm text-murekkep-2">
                  <strong>{t.kaynak_nokta_ad}</strong> gönderdiğini beyan etti,{" "}
                  <strong>{t.hedef_nokta_ad}</strong> daha az/fazla teslim
                  aldığını bildirdi. Fark, gönderen ve teslim alan noktanın
                  ortak sorumluluğundadır; onay bu ikisi arasındaki kaydı
                  KAPATIR, farkı yok saymaz.
                </p>
                {t.fark_onaylayan_id ? (
                  <p className="m-0 mt-2 text-sm text-murekkep-2">
                    Onaylandı: {tarihSaatGoster(t.fark_onay_zamani)}
                  </p>
                ) : !farkOnaylayabilir ? (
                  <p className="m-0 mt-2 text-sm text-murekkep-2">
                    Onay sizde değil: farkı{" "}
                    <strong>{ROL_ADI[Rol.KANTIN_MUDURU]}</strong>,{" "}
                    <strong>{ROL_ADI[Rol.OKUL_YONETIMI]}</strong> veya{" "}
                    <strong>{ROL_ADI[Rol.OKUL_SISTEM_YONETICISI]}</strong>{" "}
                    kapatır (KK-516). Kayıt onaya kadar açık kalır.
                  </p>
                ) : (
                  <div className="mt-3 flex flex-col gap-2">
                    <MetinAlani
                      etiket="Onay notu (denetim kaydına geçer)"
                      value={onayNotu}
                      onChange={(e) => setOnayNotu(e.target.value)}
                      rows={2}
                      ipucu="Örnek: Kırılma tespit edildi, fire kaydı ayrıca girildi."
                    />
                    <IslemDurumu hata={farkOnayla.error} />
                    <div>
                      <Buton
                        tur="tehlike"
                        onClick={() => farkOnayla.mutate()}
                        yukleniyor={farkOnayla.isPending}
                      >
                        Farkı onayla ve kapat
                      </Buton>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t border-cetvel pt-3">
              {t.durum === TransferDurumu.TASLAK && (
                <>
                  <Buton
                    tur="birincil"
                    onClick={() => gonder.mutate()}
                    yukleniyor={gonder.isPending}
                  >
                    Gönder (kaynaktan düş)
                  </Buton>
                  <Buton
                    tur="ikincil"
                    onClick={() => iptal.mutate()}
                    yukleniyor={iptal.isPending}
                  >
                    Taslağı iptal et
                  </Buton>
                </>
              )}
              {t.durum === TransferDurumu.GONDERILDI && (
                <Buton
                  tur="birincil"
                  onClick={() => setTeslimAcik((a) => !a)}
                  aria-expanded={teslimAcik}
                >
                  {teslimAcik ? "Teslim formunu kapat" : "Teslim al"}
                </Buton>
              )}
            </div>

            <IslemDurumu hata={gonder.error ?? iptal.error} />

            {teslimAcik && t.durum === TransferDurumu.GONDERILDI && (
              <TeslimFormu
                transferId={t.id}
                satirlar={t.satirlar}
                tamamlandi={() => {
                  setTeslimAcik(false);
                  tazele();
                }}
              />
            )}
          </div>
        )}
      </SorguDurumu>
    </div>
  );
}

// ---------------------------------------------------------------------------

function TeslimFormu({
  transferId,
  satirlar,
  tamamlandi,
}: {
  transferId: string;
  satirlar: TransferSatiri[];
  tamamlandi: () => void;
}) {
  const [arama, setArama] = useState("");
  const urunler = useUrunler();
  const urunHarita = new Map(urunler.data?.urunler.map((u) => [u.id, u]));
  const gorunenSatirlar = urunleriSuz(satirlar.map((s) => ({
    ...s, ad: s.urun_ad, barkodlar: urunHarita.get(s.urun_id)?.barkodlar ?? [],
  })), arama);
  const [degerler, setDegerler] = useState<Record<string, string>>(() =>
    Object.fromEntries(satirlar.map((s) => [s.urun_id, s.gonderilen_miktar])),
  );
  const [aciklama, setAciklama] = useState("");
  const [gonderildi, setGonderildi] = useState(false);

  const teslim = useMutation({
    mutationFn: () =>
      apiYaz("POST", `/transfer/${transferId}/teslim`, {
        satirlar: satirlar.map((s) => ({
          urun_id: s.urun_id,
          teslim_alinan_miktar: miktarNormalize(
            degerler[s.urun_id] ?? s.gonderilen_miktar,
          ),
        })),
        ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
      }),
    onSuccess: tamamlandi,
  });

  const gecersizVar = satirlar.some(
    (s) => !miktarGecerliMi(degerler[s.urun_id] ?? ""),
  );
  const farkVar = satirlar.some(
    (s) =>
      miktarNormalize(degerler[s.urun_id] ?? "") !== s.gonderilen_miktar &&
      miktarGecerliMi(degerler[s.urun_id] ?? ""),
  );

  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-cetvel bg-yuzey p-3"
      onSubmit={(e) => {
        e.preventDefault();
        setGonderildi(true);
        if (gecersizVar) return;
        teslim.mutate();
      }}
    >
      <p className="m-0 text-sm text-murekkep-2">
        Fiilen teslim aldığınız miktarı girin. Gönderilenle aynı değilse
        transfer <strong>farklı teslim</strong> durumuna geçer ve yönetim
        onayına düşer.
      </p>

      <AramaKutusu etiket="Teslim alınan ürün ara" ipucu="Ürün adı veya barkod" deger={arama} degistir={setArama} />
      <IslemDurumu hata={urunler.error} />
      {urunler.isPending && <p role="status">Barkodlar yükleniyor…</p>}
      {arama.trim() && <p role="status" className="m-0 text-sm text-murekkep-2">
        {gorunenSatirlar.length} satır bulundu. Arama dışında kalan satırların teslim miktarları korunur.
      </p>}

      <Tablo>
        <TabloBaslik>
          <th scope="col">Ürün</th>
          <th scope="col">Gönderilen</th>
          <th scope="col">Teslim alınan</th>
        </TabloBaslik>
        <TabloGovde>
          {gorunenSatirlar.map((s) => {
            const deger = degerler[s.urun_id] ?? "";
            const hata =
              gonderildi && !miktarGecerliMi(deger)
                ? "Miktarı 5 veya 5,500 biçiminde girin."
                : undefined;
            return (
              <tr key={s.urun_id}>
                <td className="font-semibold text-murekkep">{s.urun_ad}</td>
                <td className="rakam text-right font-mono">
                  {miktarGoster(s.gonderilen_miktar)}
                </td>
                <td>
                  <Alan
                    etiket={`${s.urun_ad} teslim alınan miktar`}
                    value={deger}
                    onChange={(e) =>
                      setDegerler((o) => ({
                        ...o,
                        [s.urun_id]: e.target.value,
                      }))
                    }
                    inputMode="decimal"
                    className="max-w-[8rem] text-right"
                    {...(hata ? { hata } : {})}
                  />
                </td>
              </tr>
            );
          })}
        </TabloGovde>
      </Tablo>

      {farkVar && (
        <Uyari tur="uyari" baslik="Fark girildi">
          Bu teslim <strong>farklı teslim</strong> olarak kaydedilecek. Fark
          ayrı bir stok hareketi olur ve yönetim onaylayana kadar açık kalır.
        </Uyari>
      )}

      <MetinAlani
        etiket="Teslim açıklaması"
        value={aciklama}
        onChange={(e) => setAciklama(e.target.value)}
        rows={2}
        ipucu="Fark varsa sebebini yazın: kırık, eksik gönderim, sayım hatası..."
      />

      <IslemDurumu hata={teslim.error} />

      <div>
        <Buton type="submit" tur="birincil" yukleniyor={teslim.isPending}>
          Teslim alındı olarak kaydet
        </Buton>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------

interface TaslakSatiri {
  /**
   * Dizin anahtarı, ortadaki satır "Kaldır" ile silinince kalan satırların alt
   * ağaçlarını kaydırır. Kararlı anahtar Sayim.tsx ve MalKabul.tsx'te de aynı
   * gerekçeyle taşınıyor.
   */
  anahtar: string;
  urunId: string;
  miktar: string;
}

/**
 * Satır DOLU sayılır: kullanıcı ona bir şey yazmıştır. Boş satır masumdur ve
 * sessizce atlanır; dolu ama geçersiz satır ASLA atlanamaz — atlanırsa taslak
 * eksik kalemle açılır, mal o kalem olmadan yola çıkar ve fark ancak teslimde
 * (KK-516) ortaya çıkar. MalKabul.tsx'te aynı kapı aynı gerekçeyle var.
 */
function doluMu(s: TaslakSatiri): boolean {
  return Boolean(s.urunId || s.miktar.trim());
}

function YeniTransfer({ olusturuldu }: { olusturuldu: (id: string) => void }) {
  const noktalar = useNoktalar({ aktif: true });
  const [kaynak, setKaynak] = useState("");
  const [hedefNokta, setHedefNokta] = useState("");
  const [sebep, setSebep] = useState(String(TransferSebebi.DAGITIM));
  const [aciklama, setAciklama] = useState("");
  const satirSayaci = useRef(1);
  const miktarAlanlari = useRef(new Map<string, HTMLInputElement>());
  const [vurgulanan, setVurgulanan] = useState<string | null>(null);
  const [satirlar, setSatirlar] = useState<TaslakSatiri[]>([
    { anahtar: "satir-1", urunId: "", miktar: "" },
  ]);
  const [gonderildi, setGonderildi] = useState(false);

  const urunler = useUrunler({ aktif: true });

  useEffect(() => {
    if (vurgulanan === null) return;
    const alan = miktarAlanlari.current.get(vurgulanan);
    alan?.focus();
    alan?.select();
    alan?.scrollIntoView({ block: "nearest" });
  }, [vurgulanan, satirlar.length]);

  const barkodOkut = (barkod: string): BarkodSonucu => {
    const urun = barkodBul(urunler.data?.urunler, barkod);
    if (!urun) return { tur: "hata", mesaj: "Bu barkoda ait aktif ürün bulunamadı. Ürünler ekranındaki barkodu kontrol edin." };
    try {
      const yeni = transferBarkoduEkle(satirlar, urun.id, `satir-${satirSayaci.current + 1}`);
      satirSayaci.current += 1;
      setSatirlar(yeni);
      const hedef = yeni.find((s) => s.urunId === urun.id)!;
      setVurgulanan(hedef.anahtar);
      // Aynı ürün tekrar okutulunca state değişmese de miktar alanına dön.
      const alan = miktarAlanlari.current.get(hedef.anahtar);
      alan?.focus();
      alan?.select();
      alan?.scrollIntoView({ block: "nearest" });
      return { tur: "onay", mesaj: `${urun.ad} · ürün seçildi; miktarı elle girin. Mevcut miktar değiştirilmedi.` };
    } catch (hata) {
      return { tur: "hata", mesaj: hata instanceof Error ? hata.message : "Ürün eklenemedi." };
    }
  };

  const doluSatirlar = satirlar.filter(doluMu);
  const gecerliSatirlar = doluSatirlar.filter(
    (s) => s.urunId !== "" && miktarGecerliMi(s.miktar),
  );
  const eksikSatirSayisi = doluSatirlar.length - gecerliSatirlar.length;

  const olustur = useMutation({
    mutationFn: () =>
      apiYaz<{ id: string }>("POST", "/transfer", {
        kaynak_nokta_id: kaynak,
        hedef_nokta_id: hedefNokta,
        sebep: Number.parseInt(sebep, 10),
        ...(aciklama.trim() ? { aciklama: aciklama.trim() } : {}),
        // Kapıyla AYNI küme: burada `s.miktar` doluluğuna bakmak, kapının
        // geçerli saymadığı bir satırı sunucuya göndermek demekti.
        satirlar: gecerliSatirlar.map((s) => ({
          urun_id: s.urunId,
          gonderilen_miktar: miktarNormalize(s.miktar),
        })),
      }),
    onSuccess: (y) => olusturuldu(y.id),
  });

  const hedefDurum = SEBEP_HEDEF_DURUM[Number.parseInt(sebep, 10)];

  return (
    <Kart baslik="Yeni transfer (taslak)">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setGonderildi(true);
          if (!kaynak || !hedefNokta || kaynak === hedefNokta) return;
          if (doluSatirlar.length === 0 || eksikSatirSayisi > 0) return;
          olustur.mutate();
        }}
      >
        <div className="grid gap-3 md:grid-cols-3">
          <Secim
            etiket="Kaynak nokta"
            value={kaynak}
            onChange={(e) => setKaynak(e.target.value)}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar, "Seçiniz")}
          />
          <Secim
            etiket="Hedef nokta"
            value={hedefNokta}
            onChange={(e) => setHedefNokta(e.target.value)}
            secenekler={noktaSecenekleri(noktalar.data?.noktalar, "Seçiniz")}
          />
          <Secim
            etiket="Sebep"
            value={sebep}
            onChange={(e) => setSebep(e.target.value)}
            secenekler={Object.entries(SEBEP_ADI).map(([k, v]) => ({
              deger: k,
              metin: v,
            }))}
          />
        </div>

        {gonderildi && kaynak && kaynak === hedefNokta && (
          <p className="m-0 text-xs text-hata" role="alert">
            Kaynak ve hedef nokta aynı olamaz.
          </p>
        )}

        {hedefDurum !== undefined && (
          <Uyari tur="bilgi" baslik="Hedef kondisyon sebebe bağlıdır">
            Bu sebeple gönderilen mal hedefte{" "}
            <strong>{STOK_DURUM_ADI[hedefDurum] ?? "—"}</strong> kondisyonunda
            açılır. Tarihi yaklaşan mal karantinaya, tarihi geçmiş mal imha
            bekliyora girer; karar verilmeden satışa dönmez.
          </Uyari>
        )}

        <section aria-label="Transfer satırları">
          <h3 className="m-0 mb-2 text-lg">Satırlar</h3>
          <BarkodAlani
            onOkut={barkodOkut}
            pasif={urunler.isPending || urunler.isError || olustur.isPending}
            pasifMetni={urunler.isError ? "Ürün listesi yüklenemedi." : "İşlem sürüyor…"}
            ipucu="Barkod ürünü seçer; miktarı elle girin. Tekrar okutmak miktarı değiştirmez. Kısayol: F2."
          />
          <IslemDurumu hata={urunler.error} />
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {satirlar.map((s, i) => (
              <li
                key={s.anahtar}
                className={`flex flex-col gap-1 ${vurgulanan === s.anahtar ? "bg-simit-yumusak" : ""}`}
                aria-current={vurgulanan === s.anahtar ? "true" : undefined}
              >
                <div className="grid gap-2 md:grid-cols-[2fr_1fr_auto]">
                  <UrunSecimi
                    etiket={`Satır ${i + 1} ürün`}
                    deger={s.urunId}
                    degistir={(urunId) =>
                      setSatirlar((o) =>
                        o.map((x, j) =>
                          j === i ? { ...x, urunId } : x,
                        ),
                      )
                    }
                    urunler={urunler.data?.urunler}
                    pasif={urunler.isPending || urunler.isError}
                  />
                  <Alan
                    ref={(el) => {
                      if (el) miktarAlanlari.current.set(s.anahtar, el);
                      else miktarAlanlari.current.delete(s.anahtar);
                    }}
                    etiket={`Satır ${i + 1} miktar`}
                    value={s.miktar}
                    onChange={(e) =>
                      setSatirlar((o) =>
                        o.map((x, j) =>
                          j === i ? { ...x, miktar: e.target.value } : x,
                        ),
                      )
                    }
                    inputMode="decimal"
                    {...(gonderildi && s.urunId && !miktarGecerliMi(s.miktar)
                      ? { hata: "Miktarı 5 veya 5,500 biçiminde girin." }
                      : {})}
                  />
                  <div className="flex items-end">
                    <Buton
                      type="button"
                      tur="sessiz"
                      onClick={() =>
                        setSatirlar((o) =>
                          o.length === 1 ? o : o.filter((_, j) => j !== i),
                        )
                      }
                      aria-label={`Satır ${i + 1} kaldır`}
                    >
                      Kaldır
                    </Buton>
                  </div>
                </div>
                {gonderildi && !s.urunId && doluMu(s) && (
                  <p className="m-0 text-xs text-hata" role="alert">
                    Bu satırda ürün seçilmedi.
                  </p>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2">
            <Buton
              type="button"
              tur="ikincil"
              onClick={() => {
                satirSayaci.current += 1;
                setSatirlar((o) => [
                  ...o,
                  {
                    anahtar: `satir-${satirSayaci.current}`,
                    urunId: "",
                    miktar: "",
                  },
                ]);
              }}
            >
              Satır ekle
            </Buton>
          </div>
          {gonderildi && doluSatirlar.length === 0 && (
            <p className="m-0 mt-2 text-xs text-hata" role="alert">
              En az bir satır girin: ürün ve miktar zorunludur.
            </p>
          )}
          {gonderildi && doluSatirlar.length > 0 && eksikSatirSayisi > 0 && (
            <p className="m-0 mt-2 text-xs text-hata" role="alert">
              Taslakta eksik veya hatalı{" "}
              <span className="rakam font-mono">{eksikSatirSayisi}</span> satır
              var. Hatalı satır sessizce atlanmaz — düzeltin veya kaldırın.
            </p>
          )}
        </section>

        <MetinAlani
          etiket="Açıklama"
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          rows={2}
        />

        <IslemDurumu hata={olustur.error} />

        <div className="flex flex-wrap items-center gap-2">
          <Buton type="submit" tur="birincil" yukleniyor={olustur.isPending}>
            Taslağı oluştur
          </Buton>
          <p className="m-0 text-xs text-murekkep-3">
            Taslak stoğu değiştirmez. Stok, &quot;Gönder&quot; adımında
            kaynaktan düşer.
          </p>
        </div>
      </form>
    </Kart>
  );
}
